import React, { useState } from 'react';
import { Plane, Clock, Shield, Users, Phone, Mail, MapPin, ChevronDown, Check, Menu, X, Utensils, Heart, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { mockData } from '../utils/mock';
import { translations } from '../translations';

export const Home = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [language, setLanguage] = useState('de');
  const t = translations[language];
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    from: '',
    to: '',
    date: '',
    passengers: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Erstelle mailto Link mit allen Formulardaten
    const subject = encodeURIComponent('Neue Fluganfrage von ' + formData.name);
    const body = encodeURIComponent(
      `Name: ${formData.name}\n` +
      `E-Mail: ${formData.email}\n` +
      `Telefon: ${formData.phone}\n` +
      `Von: ${formData.from}\n` +
      `Nach: ${formData.to}\n` +
      `Datum: ${formData.date}\n` +
      `Passagiere: ${formData.passengers}\n` +
      `Nachricht: ${formData.message}`
    );
    
    window.location.href = `mailto:booking@executive-air.eu?subject=${subject}&body=${body}`;
    
    toast.success('Vielen Dank! Wir werden uns in Kürze bei Ihnen melden.');
    setFormData({
      name: '',
      email: '',
      phone: '',
      from: '',
      to: '',
      date: '',
      passengers: '',
      message: ''
    });
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-sm">
        <div className="container mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <img 
              src="/logo.png" 
              alt="Executive Air Logo" 
              className="h-40 w-auto md:h-48 hover:scale-105 transition-transform duration-300"
            />
          </div>
          <nav className="hidden md:flex items-center space-x-8">
            <a href="#home" className="text-slate-700 hover:text-red-600 transition-colors font-medium">{t.nav.home}</a>
            <a href="#fleet" className="text-slate-700 hover:text-red-600 transition-colors font-medium">{t.nav.fleet}</a>
            <a href="#services" className="text-slate-700 hover:text-red-600 transition-colors font-medium">{t.nav.services}</a>
            <a href="#about" className="text-slate-700 hover:text-red-600 transition-colors font-medium">{t.nav.about}</a>
            <a href="#contact" className="text-slate-700 hover:text-red-600 transition-colors font-medium">{t.nav.contact}</a>
          </nav>
          <div className="flex items-center space-x-4">
            {/* Language Switcher */}
            <div className="hidden md:flex items-center space-x-2 border border-slate-300 rounded-md px-3 py-1">
              <button
                onClick={() => setLanguage('de')}
                className={`text-sm font-medium transition-colors ${
                  language === 'de' ? 'text-red-600' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                DE
              </button>
              <span className="text-slate-300">|</span>
              <button
                onClick={() => setLanguage('en')}
                className={`text-sm font-medium transition-colors ${
                  language === 'en' ? 'text-red-600' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                EN
              </button>
            </div>
            <Button className="hidden md:flex bg-red-600 hover:bg-red-700 text-white px-6 py-2" onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}>
              <Phone className="w-4 h-4 mr-2" />
              {t.nav.requestNow}
            </Button>
            <button 
              className="md:hidden text-slate-700"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-slate-200">
            <nav className="container mx-auto px-6 py-4 flex flex-col space-y-4">
              <a href="#home" className="text-slate-700 hover:text-red-600 transition-colors font-medium" onClick={() => setMobileMenuOpen(false)}>Home</a>
              <a href="#fleet" className="text-slate-700 hover:text-red-600 transition-colors font-medium" onClick={() => setMobileMenuOpen(false)}>Flotte</a>
              <a href="#services" className="text-slate-700 hover:text-red-600 transition-colors font-medium" onClick={() => setMobileMenuOpen(false)}>Services</a>
              <a href="#about" className="text-slate-700 hover:text-red-600 transition-colors font-medium" onClick={() => setMobileMenuOpen(false)}>Über Uns</a>
              <a href="#contact" className="text-slate-700 hover:text-red-600 transition-colors font-medium" onClick={() => setMobileMenuOpen(false)}>{t.footer.contact}</a>
              <Button className="bg-red-600 hover:bg-red-700 text-white w-full" onClick={() => { setMobileMenuOpen(false); document.getElementById('contact').scrollIntoView({ behavior: 'smooth' }); }}>
                <Phone className="w-4 h-4 mr-2" />
                Jetzt anfragen
              </Button>
            </nav>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section id="home" className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="/hero-jet.png"
            alt="Executive Air Beechcraft Premier 1"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent"></div>
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-3xl">

            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight animate-fade-in">
              {t.hero.title} <span className="text-red-500">{t.hero.titleHighlight}</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
              {t.hero.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg" 
                className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg font-semibold shadow-xl hover:shadow-2xl transition-all transform hover:scale-105"
                onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
              >
                {t.hero.requestFlight}
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-6 text-lg font-semibold backdrop-blur-sm transition-all"
                onClick={() => document.getElementById('fleet').scrollIntoView({ behavior: 'smooth' })}
              >
                {t.hero.discoverFleet}
              </Button>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <ChevronDown className="w-8 h-8 text-white" />
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                {t.about.title.split('.')[0]}. <span className="text-red-600">{t.about.title.split('.')[1]}.</span>
              </h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                {t.about.intro}
              </p>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                {t.about.description}
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-4 p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                      <Check className="w-6 h-6 text-red-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{t.about.availability}</h3>
                    <p className="text-gray-600">{t.about.availabilityText}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4 p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                      <Check className="w-6 h-6 text-red-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{t.about.safety}</h3>
                    <p className="text-gray-600">{t.about.safetyText}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4 p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                      <Check className="w-6 h-6 text-red-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{t.about.service}</h3>
                    <p className="text-gray-600">{t.about.serviceText}</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src={mockData.images.interior}
                alt="Executive Air Team"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-red-600 text-white p-6 rounded-xl shadow-xl">
                <p className="text-4xl font-bold mb-1">3</p>
                <p className="text-sm">{t.about.aircraftCount}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Fleet Section */}
      <section id="fleet" className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {t.fleet.title.split(':')[0]}: <span className="text-red-600">{t.fleet.title.split(':')[1]}</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t.fleet.subtitle}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <Card className="group hover:shadow-2xl transition-all duration-300 border-0 overflow-hidden">
              <div className="relative h-64 overflow-hidden">
                <img 
                  src="/cabin-midsize.png"
                  alt="Midsize Comfort Cabin"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="text-2xl font-bold text-white mb-2">{t.fleet.comfort}</h3>
                </div>
              </div>
              <CardContent className="p-6">
                <p className="text-gray-600 mb-4">{t.fleet.comfortText}</p>
                <div className="flex items-center text-red-600 font-semibold">
                  <span className="mr-2">{t.fleet.comfortStat}</span>
                </div>
              </CardContent>
            </Card>
            <Card className="group hover:shadow-2xl transition-all duration-300 border-0 overflow-hidden">
              <div className="relative h-64 overflow-hidden">
                <img 
                  src="/engine-speed.png"
                  alt="Premier Engine Speed"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="text-2xl font-bold text-white mb-2">{t.fleet.speed}</h3>
                </div>
              </div>
              <CardContent className="p-6">
                <p className="text-gray-600 mb-4">{t.fleet.speedText}</p>
                <div className="flex items-center text-red-600 font-semibold">
                  <span className="mr-2">{t.fleet.speedStat}</span>
                </div>
              </CardContent>
            </Card>
            <Card className="group hover:shadow-2xl transition-all duration-300 border-0 overflow-hidden">
              <div className="relative h-64 overflow-hidden">
                <img 
                  src="/cockpit-avionics.png"
                  alt="Cockpit Avionics"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="text-2xl font-bold text-white mb-2">{t.fleet.avionics}</h3>
                </div>
              </div>
              <CardContent className="p-6">
                <p className="text-gray-600 mb-4">{t.fleet.avionicsText}</p>
                <div className="flex items-center text-red-600 font-semibold">
                  <span className="mr-2">{t.fleet.avionicsStat}</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Comparison Table */}
          <div className="bg-gray-50 rounded-2xl p-8 md:p-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              {t.comparison.title}
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-4 px-6 font-semibold text-gray-900">{t.comparison.feature}</th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-600">{t.comparison.broker}</th>
                    <th className="text-left py-4 px-6 font-semibold text-red-600">{t.comparison.executiveAir}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-200 hover:bg-white transition-colors">
                    <td className="py-4 px-6 font-medium text-gray-900">{t.comparison.aircraft}</td>
                    <td className="py-4 px-6 text-gray-600">{t.comparison.aircraftBroker}</td>
                    <td className="py-4 px-6 text-gray-900 font-medium">{t.comparison.aircraftEA}</td>
                  </tr>
                  <tr className="border-b border-gray-200 hover:bg-white transition-colors">
                    <td className="py-4 px-6 font-medium text-gray-900">{t.comparison.cabin}</td>
                    <td className="py-4 px-6 text-gray-600">{t.comparison.cabinBroker}</td>
                    <td className="py-4 px-6 text-gray-900 font-medium">{t.comparison.cabinEA}</td>
                  </tr>
                  <tr className="border-b border-gray-200 hover:bg-white transition-colors">
                    <td className="py-4 px-6 font-medium text-gray-900">{t.comparison.speed}</td>
                    <td className="py-4 px-6 text-gray-600">{t.comparison.speedBroker}</td>
                    <td className="py-4 px-6 text-gray-900 font-medium">{t.comparison.speedEA}</td>
                  </tr>
                  <tr className="border-b border-gray-200 hover:bg-white transition-colors">
                    <td className="py-4 px-6 font-medium text-gray-900">{t.comparison.contact}</td>
                    <td className="py-4 px-6 text-gray-600">{t.comparison.contactBroker}</td>
                    <td className="py-4 px-6 text-gray-900 font-medium">{t.comparison.contactEA}</td>
                  </tr>
                  <tr className="border-b border-gray-200 hover:bg-white transition-colors">
                    <td className="py-4 px-6 font-medium text-gray-900">{t.comparison.redundancy}</td>
                    <td className="py-4 px-6 text-gray-600">{t.comparison.redundancyBroker}</td>
                    <td className="py-4 px-6 text-gray-900 font-medium">{t.comparison.redundancyEA}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {t.services.title.split(',')[0]}, <span className="text-red-600">{t.services.title.split(',')[1]}</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t.services.subtitle}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="group hover:shadow-2xl transition-all duration-300 border-0 overflow-hidden">
              <div className="relative h-64 overflow-hidden">
                <img 
                  src="/catering.png"
                  alt="Executive Catering"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
              </div>
              <CardContent className="p-6">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4">
                    <Utensils className="w-6 h-6 text-red-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">{t.services.catering}</h3>
                </div>
                <p className="text-gray-600">{t.services.cateringText}</p>
              </CardContent>
            </Card>
            <Card className="group hover:shadow-2xl transition-all duration-300 border-0 overflow-hidden">
              <div className="relative h-64 overflow-hidden">
                <img 
                  src="/pets.png"
                  alt="Pets on Board"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
              </div>
              <CardContent className="p-6">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4">
                    <Heart className="w-6 h-6 text-red-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">{t.services.pets}</h3>
                </div>
                <p className="text-gray-600">{t.services.petsText}</p>
              </CardContent>
            </Card>
            <Card className="group hover:shadow-2xl transition-all duration-300 border-0 overflow-hidden">
              <div className="relative h-64 overflow-hidden">
                <img 
                  src={mockData.images.businessJet2}
                  alt="Boarding"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
              </div>
              <CardContent className="p-6">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4">
                    <Zap className="w-6 h-6 text-red-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">{t.services.boarding}</h3>
                </div>
                <p className="text-gray-600">{t.services.boardingText}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Bereit für den <span className="text-red-600">Take-off?</span>
              </h2>
              <p className="text-xl text-gray-600">
                {t.contactForm.subtitle}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="bg-gray-50 rounded-2xl p-8 md:p-12 shadow-xl">
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Name *</label>
                  <Input 
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full"
                    placeholder="Ihr Name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">E-Mail *</label>
                  <Input 
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full"
                    placeholder="ihre@email.de"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Telefon</label>
                  <Input 
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full"
                    placeholder="+49 ..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Anzahl Passagiere</label>
                  <Input 
                    name="passengers"
                    type="number"
                    value={formData.passengers}
                    onChange={handleInputChange}
                    className="w-full"
                    placeholder="1-6"
                    min="1"
                    max="6"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Von</label>
                  <Input 
                    name="from"
                    value={formData.from}
                    onChange={handleInputChange}
                    className="w-full"
                    placeholder="Abflugort"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Nach</label>
                  <Input 
                    name="to"
                    value={formData.to}
                    onChange={handleInputChange}
                    className="w-full"
                    placeholder="Zielort"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Wunschdatum</label>
                <Input 
                  name="date"
                  type="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  className="w-full"
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Nachricht</label>
                <Textarea 
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full"
                  placeholder="Weitere Informationen oder spezielle Wünsche..."
                />
              </div>

              <Button 
                type="submit"
                size="lg"
                className="w-full bg-red-600 hover:bg-red-700 text-white py-6 text-lg font-semibold shadow-xl hover:shadow-2xl transition-all"
              >
                {t.contactForm.submit}
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-slate-800 via-slate-700 to-slate-800 text-white py-16">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div className="md:col-span-1">
              <img 
                src="/logo.png" 
                alt="Executive Air Logo" 
                className="h-40 w-auto mb-4 hover:scale-105 transition-transform duration-300"
              />
              <p className="text-gray-400 text-sm leading-relaxed">
                {t.footer.description}
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-lg mb-4">{t.footer.navigation}</h3>
              <ul className="space-y-2">
                <li><a href="#home" className="text-gray-400 hover:text-white transition-colors">Home</a></li>
                <li><a href="#fleet" className="text-gray-400 hover:text-white transition-colors">Flotte</a></li>
                <li><a href="#services" className="text-gray-400 hover:text-white transition-colors">Services</a></li>
                <li><a href="#about" className="text-gray-400 hover:text-white transition-colors">Über Uns</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold text-lg mb-4">{t.footer.contact}</h3>
              <ul className="space-y-3">
                <li className="flex items-center space-x-2 text-gray-400">
                  <Phone className="w-4 h-4" />
                  <span>+172 4331334</span>
                </li>
                <li className="flex items-center space-x-2 text-gray-400">
                  <Mail className="w-4 h-4" />
                  <span>info@executive-air.eu</span>
                </li>
                <li className="flex items-start space-x-2 text-gray-400">
                  <MapPin className="w-4 h-4 mt-1" />
                  <div>
                    <p className="font-medium text-white">Flight Operations Center (FOC)</p>
                    <p>@ Airport Trier-Foehren (EDRT)</p>
                    <p>Europa-Allee 62</p>
                    <p>D-54343 Föhren</p>
                  </div>
                </li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold text-lg mb-4">{t.footer.legal}</h3>
              <ul className="space-y-2">
                <li><a href="/impressum" className="text-gray-400 hover:text-white transition-colors">{t.footer.imprint}</a></li>
                <li><a href="/datenschutz" className="text-gray-400 hover:text-white transition-colors">{t.footer.privacy}</a></li>

              </ul>
            </div>
          </div>
          
          <div className="border-t border-slate-600 pt-8 text-center text-slate-300 text-sm">
            <p>&copy; 2025 Executive Air. Alle Rechte vorbehalten.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};